import React, { useMemo, useState, useEffect } from "react";
import FormControl from "@mui/material/FormControl";
import InputWrapper from "./InputWrapper";
import ReadOnlyInput from "./ReadOnlyInput";
import { Controller } from "react-hook-form";
import { Autocomplete, FormHelperText, TextField, Tooltip, CircularProgress } from "@mui/material";
import { DEFAULT_SIZE } from "../../../utils/constant";
import HelpIcon from "@mui/icons-material/Help";
import PropTypes from "prop-types";

const SelectFieldComponent = ({ onChange, value, options = [], label, fieldProps, error, withInfoIcon, infoMessage, placeholder, rules, loading = false, allowManualEntry = false }) => {
  const [inputValue, setInputValue] = useState("");
  useEffect(() => {
  // Whenever the React Hook form clears the field, it will reset the Autocomplete’s internal text
  if (value === "" || value === null || value === undefined) {
    setInputValue("");
  } else {
    const matchedOption = options.find(opt => opt.value === value);
    if (matchedOption) {
      setInputValue(matchedOption.label);
    }
  }
}, [value, options]);
  const safeValue = value ?? "";
  const selectedOption = options.find(option => option?.value === safeValue) || null;
  const displayValue = allowManualEntry ? (selectedOption ? selectedOption.label : inputValue) : selectedOption;
  return (
    <FormControl className="proInputWithMessage" fullWidth error={!!error}>
      <div className="proInputWithMessage-content">
        <div className={withInfoIcon ? "proInputWithMessage-input" : "proInputWithMessage-input-full"}>
          <Autocomplete
            fullWidth
            loading={loading}
            freeSolo={allowManualEntry}
            options={options}
            getOptionLabel={(option) => {
              if (typeof option === "string") return option;
              if (option?.label) return option.label;
              return "";
            }}
            isOptionEqualToValue={(option, value) => option?.value === value?.value || option?.value === value}
            value={allowManualEntry ? options.find(opt => opt.value === value) || value || null : options.find(opt => opt.value === value) || null}
            inputValue={inputValue}
            getOptionDisabled={option => option.disabled}
            onChange={(e, newValue) => {
              // Dropdown selection: set value to option.value, display to option.label
              if (typeof newValue === "object" && newValue !== null) {
                onChange(newValue.value ?? "");
                setInputValue("");
              } else if (typeof newValue === "string") {
                // Manual entry: set value and display to the string
                onChange(newValue);
                setInputValue(newValue);
              } else {
                onChange("");
                setInputValue("");
              }
            }}
            onInputChange={(e, newInputValue, reason) => {
              setInputValue(newInputValue);
              if (allowManualEntry && reason === "input") {
                onChange(newInputValue);
              }
              if (reason === "clear") {
                setInputValue("");
                onChange("");
              }
            }}
            renderInput={params => (
              <TextField
                {...params}
                error={!!error}
                label={
                  <span>
                    {label}
                    {rules?.required && <span style={{ color: "red" }}> *</span>}
                  </span>
                }
                InputLabelProps={{ shrink: true }}
                placeholder={placeholder || label}
                variant="outlined"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}          
              />
            )}
            {...fieldProps}
          />
        </div>
        {withInfoIcon && (
          <div className="proInputWithMessage-info">
            <Tooltip title={infoMessage} placement="top">
              <HelpIcon color="primary" />
            </Tooltip>
          </div>
        )}
      </div>
      <FormHelperText error={!!error}>{error?.message}</FormHelperText>
    </FormControl>
  );
};

SelectFieldComponent.propTypes = {
  onChange: PropTypes.func.isRequired,
  value: PropTypes.any,
  options: PropTypes.array.isRequired,
  label: PropTypes.string.isRequired,
  fieldProps: PropTypes.object,
  error: PropTypes.object,
  withInfoIcon: PropTypes.bool,
  infoMessage: PropTypes.string,
  placeholder: PropTypes.string
};

export default function ProSelectField({
  name,
  mode,
  label,
  rules,
  control,
  infoMessage,
  isAddMode,
  options = [],
  fieldProps = {},
  size = DEFAULT_SIZE,
  withInfoIcon = false,
  startAdornment = null,
  placeholder,
  shouldSort = false,
  loading = false,
  allowManualEntry = false,
  ...rest
}) {
  const sortOptions = options => {
    return [...options].sort((optionA, optionB) => optionA.label.localeCompare(optionB.label));
  };
  const sortedOptions = shouldSort ? sortOptions(options) : options;
  const { canView = false, canAdd = false, canEdit = false } = rest.permission || {};
  if (!canView || (isAddMode && !canAdd)) return null;
  const editModeAllowed = isAddMode ? canAdd : canEdit;
  if (mode === "read" || (mode === "edit" && !editModeAllowed))
    return <ReadOnlyInput size={size} options={options} type="select" label={label} name={name} {...rest} rules={rules} />;

  return (
    <InputWrapper size={size}>
      <Controller
        control={control}
        name={name}
        rules={rules}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <SelectFieldComponent
            onChange={onChange}
            value={value}
            placeholder={placeholder || (typeof label === "string" ? label : undefined)}
            options={sortedOptions}
            error={error}
            label={
              <span>
                {label}
                {rules?.required && <span style={{ color: "red" }}> *</span>}
              </span>
            }
            fieldProps={fieldProps}
            withInfoIcon={withInfoIcon}
            infoMessage={infoMessage}
            loading={loading}
            allowManualEntry={allowManualEntry}
          />
        )}
      />
    </InputWrapper>
  );
}

ProSelectField.propTypes = {
  name: PropTypes.string.isRequired,
  mode: PropTypes.oneOf(["read", "edit"]).isRequired,
  label: PropTypes.string.isRequired,
  control: PropTypes.object.isRequired,
  rules: PropTypes.object,
  infoMessage: PropTypes.string,
  isAddMode: PropTypes.bool,
  options: PropTypes.array,
  fieldProps: PropTypes.object,
  size: PropTypes.string,
  withInfoIcon: PropTypes.bool,
  startAdornment: PropTypes.node,
  placeholder: PropTypes.string,
  loading: PropTypes.bool,
};
